import React, { useState, useEffect } from 'react';
import { adminApi } from '../helpers/adminApi';
import WysiwygEditor from './WysiwygEditor';
import RelatedEventSelector from './RelatedEventSelector';
import { ArrowLeft } from 'lucide-react';
import { API_URL } from '../helpers/api';

const EntryForm = ({ schemaKey, entry, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState({});

  const schemaFields = {
    collections: [
      { name: 'title', type: 'text', label: 'Titre', required: true },
      { name: 'description', type: 'textarea', label: 'Description' },
      { name: 'order', type: 'number', label: 'Ordre', defaultValue: 0 },
      { name: 'mainImage', type: 'file', label: 'Image principale', accept: 'image/*' },
    ],
    events: [
      // Information principale
      { name: 'title', type: 'text', label: 'Titre', required: true, section: 'Information principale' },
      { name: 'description', type: 'textarea', label: 'Description', section: 'Information principale' },
      
      // Dates et horaires - NEW STRING FORMAT
      { 
        name: 'newStartDate', 
        type: 'datetime-string', 
        label: 'Date de début', 
        required: true, 
        section: 'Dates et horaires' 
      },
      { 
        name: 'newEndDate', 
        type: 'datetime-string', 
        label: 'Date de fin', 
        section: 'Dates et horaires' 
      },

      // Dates et horaires
      // { name: 'startDate', type: 'datetime-local', label: 'Date de début', required: true, section: 'Dates et horaires' },
      // { name: 'endDate', type: 'datetime-local', label: 'Date de fin', section: 'Dates et horaires' },
      
      // Réservation et tarifs
      { name: 'isFree', type: 'checkbox', label: 'Gratuit', section: 'Réservation et tarifs' },
      { name: 'bookingUrl', type: 'url', label: 'URL de réservation', section: 'Réservation et tarifs', defaultValue: 'https://memorial-acte.billetterie.museum' },
      
      // Médias
      { name: 'images', type: 'file', label: 'Images', accept: 'image/*', multiple: true, section: 'Médias' },
      { name: 'video', type: 'file', label: 'Vidéo', accept: 'video/*', section: 'Médias' },
      
      // Relations
      { name: 'linkedEvents', type: 'event-selector', label: 'Événements liés', section: 'Relations' },
      
      // Publication
      { name: 'isPublished', type: 'checkbox', label: 'Publier', description: 'Cocher pour publier l\'événement sur le site', section: 'Publication' },
    ],
    'hiring-ads': [
      { name: 'title', type: 'text', label: 'Titre', required: true },
      { name: 'content', type: 'textarea', label: 'Contenu' },
      { name: 'publishedAt', type: 'datetime-local', label: 'Date de publication', required: true },
      { name: 'image', type: 'file', label: 'Image (facultative)', accept: 'image/*' },
      { name: 'document', type: 'file', label: 'Documents', accept: '.pdf,.doc,.docx', multiple: true },
    ],
    magazines: [
      { name: 'title', type: 'text', label: 'Titre', required: true },
      { name: 'description', type: 'textarea', label: 'Description' },
      { name: 'publicationDate', type: 'date', label: 'Date de publication', required: true },
      { name: 'isPublished', type: 'checkbox', label: 'Publié' },
      { name: 'coverImage', type: 'file', label: 'Image de couverture', accept: 'image/*' },
      { name: 'pdfFile', type: 'file', label: 'Fichier PDF', accept: '.pdf' },
    ],
    'patrimoine-actualites': [
      { name: 'title', type: 'text', label: 'Titre', required: true },
      { name: 'content', type: 'textarea', label: 'Contenu' },
      { name: 'publishedOn', type: 'datetime-local', label: 'Date de publication', required: true },
      { name: 'images', type: 'file', label: 'Images', accept: 'image/*', multiple: true },
      { name: 'video', type: 'file', label: 'Vidéo', accept: 'video/*' },
    ],
    popups: [
      { name: 'title', type: 'text', label: 'Titre', required: true },
      { name: 'content', type: 'textarea', label: 'Contenu' },
      { name: 'startDate', type: 'datetime-local', label: 'Date de début', required: true },
      { name: 'endDate', type: 'datetime-local', label: 'Date de fin', required: true },
      { name: 'priority', type: 'number', label: 'Priorité', defaultValue: 0 },
      { name: 'active', type: 'checkbox', label: 'Actif' },
      { name: 'displayFrequency', type: 'select', label: 'Fréquence d\'affichage', options: [
        { value: 'once', label: 'Une fois' },
        { value: 'session', label: 'Par session' },
        { value: 'always', label: 'Toujours' },
      ]},
      { name: 'mainImage', type: 'file', label: 'Image principale', accept: 'image/*' },
    ],
    setting: [
      { name: 'companyName', type: 'text', label: 'Nom de l\'entreprise' },
      { name: 'phoneNumber', type: 'tel', label: 'Téléphone' },
      { name: 'contactEmail', type: 'email', label: 'Email de contact' },
      { name: 'address', type: 'textarea', label: 'Adresse' },
      { name: 'operatingHours', type: 'textarea', label: 'Heures d\'ouverture' },
      { name: 'pricingDetails', type: 'textarea', label: 'Détails des tarifs' },
      { name: 'defaultBookingUrl', type: 'url', label: 'URL de réservation par défaut' },
      { name: 'facebook', type: 'url', label: 'Facebook' },
      { name: 'twitter', type: 'url', label: 'Twitter' },
      { name: 'instagram', type: 'url', label: 'Instagram' },
      { name: 'youtube', type: 'url', label: 'YouTube' },
      { name: 'linkedin', type: 'url', label: 'LinkedIn' },
      { name: 'whatsapp', type: 'text', label: 'WhatsApp' },
      { name: 'logo', type: 'file', label: 'Logo', accept: 'image/*' },
      { name: 'heroVideo', type: 'file', label: 'Vidéo héro', accept: 'video/*' },
      { name: 'decouvrirVideo', type: 'file', label: 'Vidéo découvrir', accept: 'video/*' },
    ],
    'special-menu-link': [
      { name: 'name', type: 'text', label: 'Nom' },
      { name: 'url', type: 'url', label: 'URL' },
      { name: 'active', type: 'checkbox', label: 'Actif' },
    ],
  };

  const fields = schemaFields[schemaKey] || [];

  useEffect(() => {
    if (entry) {
      const initialData = { ...entry };
      
      // Convert dates for datetime-local inputs
      fields.forEach(field => {
        // Normalize single media to an array for multiple file fields
        // (backward compat: old entries may return a single object)
        if (field.type === 'file' && field.multiple && initialData[field.name] && !Array.isArray(initialData[field.name])) {
          initialData[field.name] = [initialData[field.name]];
        }

        // Handle new string datetime fields (no conversion needed!)
        if (field.type === 'datetime-string' && initialData[field.name]) {
          // It's already in the format "YYYY-MM-DD HH:MM" - just use it as-is
          console.log(`[Load String DateTime] ${field.name}:`, initialData[field.name]);
          // No conversion needed!
        }

        if (field.type === 'datetime-local' && initialData[field.name]) {
          const dateString = initialData[field.name];
          console.log(`[Load] Original date string for ${field.name}:`, dateString);
          
          // The date is stored as ISO string in UTC
          // Convert it to local time like EntryList does
          const date = new Date(dateString);
          
          // Get the LOCAL components (this automatically converts from UTC to local)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          
          // Format for datetime-local input
          initialData[field.name] = `${year}-${month}-${day}T${hours}:${minutes}`;
          
          console.log(`[Load] Formatted for input:`, initialData[field.name]);
          console.log(`[Load] This should match what EntryList shows`);
        }
        if (field.type === 'date' && initialData[field.name]) {
          // Extract just the date part
          const dateString = initialData[field.name];
          if (dateString.includes('T')) {
            initialData[field.name] = dateString.substring(0, 10);
          } else if (dateString.length >= 10) {
            initialData[field.name] = dateString.substring(0, 10);
          }
        }
      });
      
      
      setFormData(initialData);
    } else {
      const initialData = {};
      fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          initialData[field.name] = field.defaultValue;
        }
      });
      setFormData(initialData);
    }
  }, [entry, schemaKey]); // Removed fields dependency to prevent re-render on every keystroke

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWysiwygChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEventSelectorChange = (name, selectedEvents) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedEvents
    }));
  };

  const handleFileUpload = async (e, fieldName) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(prev => ({ ...prev, [fieldName]: true }));
    
    try {
      const uploadPromises = files.map(file => adminApi.uploadFile(file));
      const results = await Promise.all(uploadPromises);
      
      const field = fields.find(f => f.name === fieldName);
      if (field?.multiple) {
        // Append new files to existing ones
        setFormData(prev => ({
          ...prev,
          [fieldName]: [...(prev[fieldName] || []), ...results.flat()]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [fieldName]: results[0]?.[0]
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Erreur lors du téléchargement du fichier');
    } finally {
      setUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleRemoveFile = (fieldName, index) => {
    setFormData(prev => {
      const files = [...(prev[fieldName] || [])];
      files.splice(index, 1);
      return {
        ...prev,
        [fieldName]: files
      };
    });
  };

  const handleMoveFile = (fieldName, index, direction) => {
    setFormData(prev => {
      const files = [...(prev[fieldName] || [])];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (newIndex < 0 || newIndex >= files.length) return prev;
      
      // Swap items
      [files[index], files[newIndex]] = [files[newIndex], files[index]];
      
      return {
        ...prev,
        [fieldName]: files
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Block saving when the end date is before the start date.
    // Both formats ("YYYY-MM-DD HH:MM" and "YYYY-MM-DDTHH:mm") compare correctly as strings.
    const datePairs = [['newStartDate', 'newEndDate'], ['startDate', 'endDate']];
    for (const [startName, endName] of datePairs) {
      const hasPair = fields.some(f => f.name === startName) && fields.some(f => f.name === endName);
      const start = formData[startName];
      const end = formData[endName];
      if (hasPair && start && end && end < start) {
        setError('La date de fin est antérieure à la date de début. Vérifiez les dates (notamment l\'année).');
        return;
      }
    }

    setLoading(true);

    try {
      // Clean the form data by removing system fields
      const cleanedData = { ...formData };
      
      // Remove all system fields that Strapi doesn't accept
      const systemFields = [
        'id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt', 
        'locale', 'localizations', 'createdBy', 'updatedBy'
      ];
      
      systemFields.forEach(field => {
        delete cleanedData[field];
      });
      
      // Process datetime-local fields - let JavaScript handle the conversion naturally
      fields.forEach(field => {
        // NEW: String datetime fields - no conversion needed!
        if (field.type === 'datetime-string' && cleanedData[field.name]) {
          // It's already in "YYYY-MM-DD HH:MM" format - send as-is
          console.log(`[Save String DateTime] ${field.name}:`, cleanedData[field.name]);
          // No conversion needed!
        }

        if (field.type === 'datetime-local' && cleanedData[field.name]) {
          // The input value is in format YYYY-MM-DDTHH:mm
          const localValue = cleanedData[field.name];
          
          // Create a date object - JavaScript will interpret this as local time
          const localDate = new Date(localValue);
          
          // Convert to ISO string - this will convert to UTC automatically
          cleanedData[field.name] = localDate.toISOString();
          
          console.log(`[Save] Original input: ${localValue}`);
          console.log(`[Save] Local date object: ${localDate}`);
          console.log(`[Save] Saving as UTC: ${cleanedData[field.name]}`);
        }
      });
      
      console.log('Original form data:', formData); // Debug log
      console.log('Cleaned form data being submitted:', cleanedData); // Debug log
      
      await onSubmit(cleanedData);
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const renderFilePreview = (file, isMultiple = false, fieldName = '') => {
    if (!file) return null;
    
    if (isMultiple && Array.isArray(file)) {
      return (
        <div className="mt-3">
          <div className="row g-2">
            {file.map((f, index) => (
              <div key={index} className="col-md-4 col-sm-6">
                <div className="card h-100">
                  <div className="position-relative">
                    {f.mime?.includes('image') ? (
                      <img 
                        src={`${API_URL}${f.url}`} 
                        alt={f.name} 
                        className="card-img-top"
                        style={{ height: '150px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: '150px' }}>
                        <span className="badge bg-secondary fs-1">📄</span>
                      </div>
                    )}
                    <div className="position-absolute top-0 end-0 p-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveFile(fieldName, index)}
                        title="Supprimer"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-2">
                    <small className="text-truncate d-block mb-2">{f.name}</small>
                    <div className="btn-group btn-group-sm w-100" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => handleMoveFile(fieldName, index, 'up')}
                        disabled={index === 0}
                        title="Déplacer vers la gauche"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => handleMoveFile(fieldName, index, 'down')}
                        disabled={index === file.length - 1}
                        title="Déplacer vers la droite"
                      >
                        →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className="mt-2">
        <div className="d-flex align-items-center">
          {file.mime?.includes('image') ? (
            <img 
              src={`${API_URL}${file.url}`} 
              alt={file.name} 
              className="img-thumbnail me-2"
              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
            />
          ) : (
            <span className="badge bg-secondary me-2">📄</span>
          )}
          <small>{file.name}</small>
        </div>
      </div>
    );
  };

  const renderFieldsBySections = () => {
    // Group fields by section
    const fieldsBySection = fields.reduce((acc, field) => {
      const section = field.section || 'Général';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(field);
      return acc;
    }, {});

    // If no sections defined, render fields as before
    if (Object.keys(fieldsBySection).length === 1 && fieldsBySection['Général']) {
      return (
        <div className="row">
          {fields.map((field) => {
            const isFullWidth = field.name === 'title' || 
                              field.name === 'description' || 
                              field.name === 'content';
            
            return (
              <div key={field.name} className={isFullWidth ? "col-12 mb-3" : "col-md-6 mb-3"}>
                <label className="form-label">
                  {field.label}
                  {field.required && <span className="text-danger">*</span>}
                </label>
                {renderField(field)}
              </div>
            );
          })}
        </div>
      );
    }

    // Render fields grouped by sections
    return Object.entries(fieldsBySection).map(([sectionName, sectionFields]) => (
      <div key={sectionName} className="mb-4">
        <h6 className="text-muted mb-3 pb-2 border-bottom">{sectionName}</h6>
        <div className="row">
          {sectionFields.map((field) => {
            const isFullWidth = field.name === 'title' || 
                              field.name === 'description' || 
                              field.name === 'content' ||
                              field.type === 'textarea' ||
                              field.type === 'event-selector';
            
            return (
              <div key={field.name} className={isFullWidth ? "col-12 mb-3" : "col-md-6 mb-3"}>
                <label className="form-label">
                  {field.label}
                  {field.required && <span className="text-danger">*</span>}
                </label>
                {renderField(field)}
              </div>
            );
          })}
        </div>
      </div>
    ));
  };

  const renderField = (field) => {
    const { name, type, label, required, accept, multiple, options } = field;
    
    switch (type) {
      case 'textarea':
        return (
          <WysiwygEditor
            value={formData[name] || ''}
            onChange={(value) => handleWysiwygChange(name, value)}
            placeholder={`Tapez votre ${label.toLowerCase()}...`}
          />
        );
      
      case 'select':
        return (
          <select
            className="form-select"
            name={name}
            value={formData[name] || ''}
            onChange={handleInputChange}
            required={required}
          >
            <option value="">Sélectionnez...</option>
            {options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name={name}
              id={`checkbox-${name}`}
              checked={formData[name] || false}
              onChange={handleInputChange}
            />
            <label className="form-check-label" htmlFor={`checkbox-${name}`}>
              {label}
            </label>
            {field.description && (
              <small className="text-muted d-block">{field.description}</small>
            )}
          </div>
        );
      
      case 'file':
        return (
          <div>
            <input
              className="form-control"
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={(e) => handleFileUpload(e, name)}
              disabled={uploading[name]}
            />
            {uploading[name] && (
              <small className="text-muted">Téléchargement en cours...</small>
            )}
            {formData[name] && (
              <small className="text-success d-block mt-1">
                {multiple ? `${formData[name].length} fichier(s) sélectionné(s)` : 'Fichier sélectionné'}
              </small>
            )}
            {renderFilePreview(formData[name], multiple, name)}
          </div>
        );
      
      case 'event-selector':
        return (
          <RelatedEventSelector
            value={formData[name] || []}
            onChange={(selectedEvents) => handleEventSelectorChange(name, selectedEvents)}
            currentEventId={entry?.documentId}
          />
        );
      
      case 'datetime-string':
        // Split the string datetime into date and time for separate inputs
        const dateTimeValue = formData[name] || '';
        const [datePart, timePart] = dateTimeValue.split(' ');
        
        return (
          <div className="row g-2">
            <div className="col-md-6">
              <input
                className="form-control"
                type="date"
                value={datePart || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  const existingTime = timePart || '00:00';
                  handleInputChange({
                    target: {
                      name,
                      value: newDate ? `${newDate} ${existingTime}` : '',
                      type: 'text'
                    }
                  });
                }}
                required={required}
              />
            </div>
            <div className="col-md-6">
              <input
                className="form-control"
                type="time"
                value={timePart || ''}
                onChange={(e) => {
                  const newTime = e.target.value;
                  const existingDate = datePart || '';
                  if (existingDate) {
                    handleInputChange({
                      target: {
                        name,
                        value: `${existingDate} ${newTime}`,
                        type: 'text'
                      }
                    });
                  }
                }}
                required={required && datePart}
              />
            </div>
            {dateTimeValue && (
              <div className="col-12">
                <small className="text-muted">
                  Format enregistré: {dateTimeValue}
                </small>
              </div>
            )}
          </div>
        );

      default:
        return (
          <input
            className="form-control"
            type={type}
            name={name}
            value={formData[name] || ''}
            onChange={handleInputChange}
            required={required}
          />
        );
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            {entry ? 'Modifier' : 'Ajouter'} {schemaKey}
          </h5>
          {onCancel && (
            <button
              type="button"
              className="btn btn-link text-muted p-0 d-flex align-items-center"
              onClick={onCancel}
              title="Retour à la liste"
            >
              <ArrowLeft size={18} className="me-1" />
              Retour
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {renderFieldsBySections()}
          
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          <div className="d-flex gap-2 mt-4">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            {onCancel && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={loading}
              >
                Annuler
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntryForm;